import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { evalCartType } from '@elab/core/utils/cart/CartUtils';
import logger from '@elab/core/utils/logger';
import constants, {
    NO_INFO_STOCK,
    adjustConfirmationDialog,
    adjustMaxAllowedProducts,
    cookieNames,
    orderConfirmationUrl,
    paymentErrors,
    paymentSlots,
} from '@elab/core/utils/constants';
import isEmpty from '@elab/core/utils/isEmpty';
import * as address from '@elab/core/utils/address';
import * as storage from '@elab/core/utils/storage';
import { actions as uiActions } from '@elab/core/store/ui';
import {
    actions as myAccountActions,
    selectors as myAccountSelectors,
} from '@elab/core/store/my-account';
import { actions as orderActions } from '@elab/core/store/order';
import { selectors as paymentSelectors } from '@elab/core/store/payment';

import { actions as pickUpInStoreActions } from '../../components/connected/PickUpInStoreSelectors/redux';
import { getSelectors as giftCardSelectors } from '../../components/connected/forms/GiftCardForm/redux';
import { actions as cartActions, selectors as cartSelectors, types as cartTypes } from '../cart';
import { getEGCcartId } from '../cart/saga';
import { selectors as userSelectors } from '../user';
import { selectors as configSelectors } from '../config';
import { getUserId } from '../../utils/user/sagaHelpers';
import { actions as globalActions, selectors as globalSelectors } from '../global';
import analytics from '../../utils/analytics';
import { actions as analyticsActions } from '../analytics';
import { getCookie, setCookie } from '../../utils/cookie';
import { getMobilePlatformType } from '../../utils/device';
import uiTypes from '../ui/types';

import klarnaApiLoader from './klarnaApiLoader';
import { klarnaAuthorize, klarnaInitLoadWidget, klarnaMethodIsLoaded } from './klarnaHelpers';

import { actions, api, selectors, types } from '.';

let DIALOG_NAME = adjustConfirmationDialog;

export function* getPaymentMethods({ payload }) {
    const { isPreselectedMethod } = payload.options;
    const userId = yield call(getUserId);
    const eGiftCardCartId = yield call(getEGCcartId);
    const isEgiftCardCartActive = !!eGiftCardCartId;
    let cartId = yield select(userId ? cartSelectors.getCode : cartSelectors.getGuid);
    if (isEgiftCardCartActive) {
        // cardId is taken directly from cookie because when click back in browser from Gift Card PDP to checkout, the store is not yet updated
        cartId = eGiftCardCartId;
    }
    const dynamicGiftCardsStatus = yield select(configSelectors.getDynamicGiftCardsStatus);

    try {
        const { paymentModes = [], tokenizationAllowed } = yield call(
            api.getMethods,
            userId,
            cartId,
            isEgiftCardCartActive,
        );
        paymentModes.sort((a, b) => a.position - b.position);
        yield put(actions.setPaymentMethods(paymentModes));
        yield put(actions.setTokenizationAllowed(!!tokenizationAllowed));
        if (isPreselectedMethod) {
            const preselectedMode = paymentModes.find(({ code }) => code !== 'giftcard');
            yield put(actions.setPaymentMethod(preselectedMode));
        }
        if (dynamicGiftCardsStatus) yield put(actions.getGiftCardMethods());
        yield call(setLoyaltyPointsPayMethod, userId, cartId);

        if (klarnaMethodIsLoaded(paymentModes)) {
            yield put(actions.loadKlarnaApi());
        }

        const selectedPaymentMethod = yield select(selectors.getSelectedMethod);
        if (
            selectedPaymentMethod.isKlarnaAuth &&
            selectedPaymentMethod.code &&
            selectedPaymentMethod.integrationType === paymentSlots.REDIRECT &&
            selectedPaymentMethod.code.includes(paymentSlots.KLARNA)
        ) {
            yield put(actions.updatePaymentSession(selectedPaymentMethod));
        }
    } catch (e) {
        yield call(logger.error, e);
    }
}

export function* setLoyaltyPointsPayMethod(userId, cartId) {
    if (!cartId) {
        userId = yield call(getUserId);
        const eGiftCardCartId = yield call(getEGCcartId);
        const isEgiftCardCartActive = !!eGiftCardCartId;
        cartId = yield select(userId ? cartSelectors.getCode : cartSelectors.getGuid);
        if (isEgiftCardCartActive) {
            // cardId is taken directly from cookie because when click back in browser from Gift Card PDP to checkout, the store is not yet updated
            cartId = eGiftCardCartId;
        }
    }
    const payWithLoyaltyPointsMethod = yield select(selectors.getPayWithLoyaltyPointsMethod);
    if (payWithLoyaltyPointsMethod) {
        const payWithLoyaltyPointsMethodExtraInfo = yield call(
            api.getPayWithLoyaltyPointsMethodExtraInfo,
            userId,
            cartId,
        );
        yield put(
            actions.setPayWithLoyaltyPointsMethodExtraInfo(payWithLoyaltyPointsMethodExtraInfo),
        );
    }
}

function* watchGetPaymentMethods() {
    yield takeEvery(types.GET_PAYMENT_METHODS, getPaymentMethods);
}

export const validationWrapper = (cb, placeOrder = false) =>
    function*(action = {}) {
        const isSubmitting = yield select(globalSelectors.isSubmitting);
        if (isSubmitting) return;

        const [cardWithError] = yield select(giftCardSelectors('gift-card').getCardsWithError);

        if (cardWithError) {
            const giftCards = yield select(cartSelectors.getGiftCardTransactions);

            const indexOfError = giftCards.findIndex(
                el => el.giftCard.cardNumber === cardWithError,
            );
            const sortedArray = [
                ...giftCards.splice(indexOfError, giftCards.length - 1),
                ...giftCards.splice(0, indexOfError + 1),
            ];

            yield put(cartActions.setDataAttr('giftCardTransactions', sortedArray));

            return;
        }

        yield put(globalActions.setSubmitting(true));
        const { failureRedirectUrl } = action.payload || {};
        yield put(cartActions.loadCart());
        yield take(cartTypes.SET_LOADED);
        const stopAdjustCartOnClose = yield select(configSelectors.getStopAdjustCartOnClose);
        const outOfStockProducts = yield select(cartSelectors.getHaveOutOfStock);
        const isNoInfo = yield select(cartSelectors.getNoStock, NO_INFO_STOCK);
        const configDialogName = yield select(configSelectors.getAdjustDialogName);
        DIALOG_NAME =
            configDialogName === adjustMaxAllowedProducts ? adjustMaxAllowedProducts : DIALOG_NAME;
        const shouldProceed = yield call(checkForMaxAllowedQuantityForDelivery);

        if (shouldProceed === 'PROCEED_PLACE_ORDER') {
            if (outOfStockProducts && !isNoInfo) {
                const hasAdjustConfirmationDialog = yield select(
                    configSelectors.hasAdjustConfirmationDialog,
                );
                if (placeOrder && hasAdjustConfirmationDialog) {
                    yield put(uiActions.openDialog({ name: DIALOG_NAME }));
                    const action = yield take([cartTypes.ADJUST_CART, uiTypes.CLOSE_DIALOG]);

                    if (action.type === uiTypes.CLOSE_DIALOG && !stopAdjustCartOnClose) {
                        yield put({
                            ...cartActions.adjustCart(),
                            payload: {
                                failureRedirectUrl: '/cart',
                                successRedirectUrl: '/cart',
                            },
                        });
                        return yield put(globalActions.setSubmitting(false));
                    }
                } else {
                    yield put(globalActions.redirect('/cart'));
                    return yield put(globalActions.setSubmitting(false));
                }
            }
        } else {
            if (shouldProceed === cartTypes.ADJUST_CART) {
                yield take(cartTypes.SET_CART_DATA);
            } else if (shouldProceed === uiTypes.CLOSE_DIALOG) {
                return yield put(globalActions.setSubmitting(false));
            } else {
                yield put(globalActions.redirect(failureRedirectUrl));
                return yield put(globalActions.setSubmitting(false));
            }
        }
        const isCheckoutBlocked = yield select(cartSelectors.isCheckoutBlocked);
        const expressPickupValidation = yield select(
            cartSelectors.getExpressPickupValidationMessage,
        );
        if (isCheckoutBlocked || !!expressPickupValidation.length) {
            yield put(globalActions.redirect(failureRedirectUrl));
            return yield put(globalActions.setSubmitting(false));
        }

        try {
            yield call(cb, action);
        } catch (e) {
            yield put(globalActions.redirect(failureRedirectUrl));
        }

        yield put(globalActions.setSubmitting(false));
    };

function* checkForMaxAllowedQuantityForDelivery() {
    const { code } = yield select(cartSelectors.getDeliveryMode);
    const pointOfService = yield select(cartSelectors.getDeliveryPointOfService);
    const cartItems = yield select(cartSelectors.getEntries);
    const maxQuantity = yield select(cartSelectors.maxAllowedProductQuantity);
    const outOfStockEntries = yield select(cartSelectors.getOutOfStockEntries);
    let hasOutOfStock = !!outOfStockEntries.length;
    if (
        (maxQuantity || hasOutOfStock) &&
        (!code.includes(constants.ccDeliveryName) ||
            (code.includes(constants.ccDeliveryName) && pointOfService.pickupExpressEligible))
    ) {
        yield put(cartActions.setHaveOutOfStock(hasOutOfStock));
        const hasProductQuantityThanMaxAllowed =
            cartItems.length && cartItems.some(({ quantity }) => quantity > maxQuantity);
        if (hasProductQuantityThanMaxAllowed || hasOutOfStock) {
            yield put(uiActions.openDialog({ name: DIALOG_NAME }));
            const action = yield take([cartTypes.ADJUST_CART, uiTypes.CLOSE_DIALOG]);

            return action.type;
        }
    }
    return 'PROCEED_PLACE_ORDER';
}

export function* placeOrder(userId, cartId) {
    const shouldSubscribe = yield select(selectors.shouldSubscribe);
    const DTOplaceOrder = {
        termsCheck: yield select(selectors.termsAccepted),
    };
    const PrivacySelection = yield select(configSelectors.getPrivacySelection);
    if (PrivacySelection.isPrivacyDefault) DTOplaceOrder.privacyPolicyAccepted = true;

    try {
        yield put(actions.setLoading(true));
        const mobilePlatform = yield call(getMobilePlatformType);
        yield call(
            api.placeOrder,
            userId,
            cartId,
            shouldSubscribe,
            DTOplaceOrder,
            false,
            !mobilePlatform,
        );

        const isEgiftCardCartActive = yield call(getEGCcartId);
        if (isEgiftCardCartActive) {
            yield put(cartActions.restoreRegularCart());
        }
        yield put(cartActions.loadCart());
    } catch (e) {
        const {
            json: { errors },
        } = e;
        const { type, message } = errors[0];

        if (type === 'ZeroBalanceGiftCardError') {
            yield put(actions.setError(paymentErrors.zeroBalanceGiftCardError, message));
        }
    } finally {
        yield put(actions.setLoading(false));
    }
}

const getAdditionalParameter = (card, cardHolderName, token, saveCard, paymentInfoId) => {
    let entry = [
        {
            key: 'token',
            value: token,
        },
        {
            key: 'cardHolderFullName',
            value: cardHolderName,
        },
    ];
    if (paymentInfoId) {
        entry = [
            ...entry,
            {
                key: 'paymentInfoId',
                value: paymentInfoId,
            },
        ];
    }
    if (card) {
        entry = [
            ...entry,
            {
                key: 'cardNumber',
                value: card.cardNumber,
            },
            {
                key: 'expirationMonth',
                value: card.expiryMonth,
            },
            {
                key: 'issueMonth',
                value: '01', // These value will remain silent
            },
            {
                key: 'issueYear',
                value: '19', // These value will remain silent
            },
            {
                key: 'expirationYear',
                value: card.expiryYear,
            },
            {
                key: 'cardType',
                value: card.cardType.code.toUpperCase(),
            },
        ];
    }
    if (saveCard) {
        entry.push({
            key: 'save_card',
            value: true,
        });
    }
    return { entry };
};

function* getKlarnaAuth() {
    const selectedPaymentMethod = yield select(selectors.getSelectedMethod);

    const sessionData = yield select(selectors.getPaymentSession);

    const purchaseCountry = yield select(globalSelectors.getCountry);
    const purchaseCurrency = yield select(globalSelectors.getCurrencyCode);
    const purchaseLocale = yield select(globalSelectors.getLanguage);
    const imagePlaceholderPath = yield select(globalSelectors.getImagePlaceholderPath);
    const paymentAddress = yield select(cartSelectors.getPaymentAddress);
    const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);
    const deliveryMode = yield select(cartSelectors.getDeliveryMode);
    const isDeliveryModePickup = yield select(cartSelectors.isDeliveryModePickup);
    const totalPrice = yield select(cartSelectors.getTotalPrice);
    const totalTax = yield select(cartSelectors.getTotalTax);
    const entries = yield select(cartSelectors.getEntries);
    const amountToPayValue = yield select(cartSelectors.getAmountToPayValue);
    const giftOptionsTotal = yield select(cartSelectors.getCartSummaryGiftOptions);

    const dateOfBirth = yield select(userSelectors.getDateOfBirth);

    const cartData = {
        purchaseCountry,
        purchaseCurrency,
        purchaseLocale,

        paymentAddress,
        deliveryAddress,
        deliveryMode,
        isDeliveryModePickup,
        totalPrice,
        totalTax,
        entries,

        dateOfBirth,
        imagePlaceholderPath,
        amountToPayValue,
        giftOptionsTotal,
    };

    const authorizationResponse = yield call(klarnaAuthorize, sessionData, cartData);

    return authorizationResponse;
}

export function* placeNewOrder(userId, cartId, successRedirectUrl) {
    const shouldSubscribe = yield select(selectors.shouldSubscribe);
    const PrivacySelection = yield select(configSelectors.getPrivacySelection);
    const iFrameFlow = yield select(configSelectors.iframeRedirection);
    const termsCheck = yield select(selectors.termsAccepted) || PrivacySelection.isPrivacyDefault;
    const gdprServiceInformation = { termsCheck };
    const selectedCard = yield select(selectors.getSelectedCard);
    const token = yield select(selectors.getIFrameToken);
    const distanceSellingContract = yield select(selectors.getdistanceSellingContract);
    const preliminaryInformationForm = yield select(selectors.getPreliminaryInformationForm);
    const deviceTypeFlag = yield select(configSelectors.deviceTypeFlag);
    const selectedPaymentMethod = yield select(selectors.getSelectedMethod);

    if (PrivacySelection.isPrivacyDefault) gdprServiceInformation.privacyPolicyAccepted = true;
    const detectedDevice = yield call(getMobilePlatformType);
    const body = {
        gdprServiceInformation,
        legalInformation: [
            { name: 'distanceSellingContract', accepted: distanceSellingContract },
            { name: 'preliminaryInformationForm', accepted: preliminaryInformationForm },
        ],
        ...(deviceTypeFlag && { detectedDevice: detectedDevice !== '' ? 'MOBILE' : '' }),
    };

    if ((selectedCard || token !== '') && selectedCard.id !== 'NEW_CARD') {
        const cardHolderName = yield select(selectors.getCardHolderName);
        const saveCard = yield select(selectors.getSavedCardFlag);
        const { id: paymentInfoId } = yield select(selectors.getSelectedCard);
        body.additionalPaymentParameters = getAdditionalParameter(
            selectedCard,
            cardHolderName,
            token,
            saveCard,
            paymentInfoId,
        );
    }

    const isSaferpayCard =
        selectedPaymentMethod.code &&
        selectedPaymentMethod.code.toLowerCase().includes(paymentSlots.SAFERPAY_CARD);

    const saveCard = yield select(selectors.getSavedCardFlag);
    if (saveCard && isSaferpayCard && selectedCard && selectedCard.id === 'NEW_CARD') {
        body.additionalPaymentParameters = {
            entry: [
                {
                    key: 'save_card',
                    value: true,
                },
            ],
        };
    }

    if (iFrameFlow) {
        body.additionalPaymentParameters = {
            userAgent: navigator.userAgent,
        };
    }

    if (
        selectedPaymentMethod.isKlarnaAuth &&
        selectedPaymentMethod.code &&
        selectedPaymentMethod.integrationType === paymentSlots.REDIRECT &&
        selectedPaymentMethod.code.includes(paymentSlots.KLARNA)
    ) {
        const sessionData = yield select(selectors.getPaymentSession);

        const token = sessionData.authToken;
        const paymentMethodCategory =
            sessionData.paymentCategories.length && sessionData.paymentCategories[0].identifier;

        body.additionalPaymentParameters = {
            entry: [
                {
                    key: 'token',
                    value: token,
                },
                {
                    key: 'paymentMethodCategory',
                    value: paymentMethodCategory,
                },
            ],
        };
    }

    try {
        yield put(actions.setLoading(true));

        const OrderResponse = yield call(
            api.placeOrder,
            userId,
            cartId,
            shouldSubscribe,
            body,
            true,
            !detectedDevice,
        );

        if (
            selectedPaymentMethod.isKlarnaAuth &&
            selectedPaymentMethod.code &&
            selectedPaymentMethod.code.includes(paymentSlots.KLARNA) &&
            selectedPaymentMethod.integrationType === paymentSlots.REDIRECT
        ) {
            const klarnaAuthorizationResponse = yield call(getKlarnaAuth);

            if (klarnaAuthorizationResponse.approved) {
                const paymentStatus = yield call(getPaymentStatus, OrderResponse.code);
                if (paymentStatus.url.includes(orderConfirmationUrl.CONFIRMATION)) {
                    yield put(globalActions.redirect(paymentStatus.url));
                } else {
                    yield put(uiActions.scrollToTopOfPage());
                    yield put(actions.setError('klarnaAuthorizationError'));
                }
            } else {
                yield call(logger.error, klarnaAuthorizationResponse);
                if (
                    !klarnaAuthorizationResponse.approved &&
                    !klarnaAuthorizationResponse.show_form
                ) {
                    yield put(actions.payNowActive(false));
                    yield call(deletePaymentMethod);
                    yield put(uiActions.scrollToTopOfPage());
                    yield put(actions.setError('klarnaDenyError'));
                } else {
                    yield put(uiActions.scrollToTopOfPage());
                    yield put(actions.setError('klarnaAuthorizationError'));
                }
            }
        }

        const isEgiftCardCartActive = !!(yield call(getEGCcartId));
        if (!isEgiftCardCartActive) {
            yield put(globalActions.setOriginAction(types.ORDER_PLACED));
            yield put(cartActions.loadCart());
        }
        yield put(actions.resetCardSelection());
        if (OrderResponse.paymentUrl) {
            if (iFrameFlow) {
                yield put(globalActions.goto(successRedirectUrl));
            } else {
                yield put(globalActions.redirect(OrderResponse.paymentUrl));
            }
        } else {
            // if go directly to order confirmation,
            // we need to restore the regular cart if EGiftCard checkout is active
            if (isEgiftCardCartActive) {
                yield put(cartActions.restoreRegularCart({ shouldLoadCart: true }));
            }
            if (OrderResponse.redirectUrl) {
                yield put(globalActions.redirect(OrderResponse.redirectUrl.split(':').pop()));
            }
        }
    } catch (e) {
        yield call(handlePlaceOrderFailureScenario, e, successRedirectUrl);
    } finally {
        yield put(actions.setLoading(false));
    }
}

function* getPaymentStatus(orderId) {
    const sessionData = yield select(selectors.getPaymentSession);
    const paymentStatus = yield call(api.getPaymentStatus, orderId, sessionData.sessionId);
    return paymentStatus;
}

function* handlePlaceOrderFailureScenario(e, successRedirectUrl, redirect = true) {
    const {
        json: { errors },
    } = e;
    const { type, message } = errors[0];

    if (type === 'ZeroBalanceGiftCardError') {
        yield put(actions.setError(paymentErrors.zeroBalanceGiftCardError, message));
    } else if (type === 'PlaceOrderCartValidationResponseError') {
        yield put(actions.setError(paymentErrors.placeOrderCartValidationResponseError, message));
        yield put(cartActions.loadCart());
    } else {
        yield call(storage.set, storage.key.orderApiError, true);
        yield put(actions.setError(paymentErrors.orderFailedText, message));
        // we need to send user to next URL for any case, so that validation for PSP
        // error can be done.
        if (redirect) {
            yield put(globalActions.goto(successRedirectUrl));
        }
    }
    yield put(uiActions.scrollToTopOfPage());
}

export function* putPaymentMode({ payload }) {
    const isPlaceOrderBlocked = yield select(cartSelectors.isPlaceOrderBlocked);
    if (isPlaceOrderBlocked) {
        const url = yield call(address.getURL);
        yield put(globalActions.redirect(url));
        return;
    }

    const userId = yield call(getUserId);
    const cartId = yield select(userId ? cartSelectors.getCode : cartSelectors.getGuid);
    const paymentMode = yield select(selectors.getSelectedMethod);

    const cartType = yield select(cartSelectors.getCartType);
    const cartTotalPrice = yield select(cartSelectors.getTotalPrice);

    yield call(api.setPaymentMethod, userId, cartId, paymentMode.code);
    yield call(placeOrder, userId, cartId);

    if (evalCartType(cartType, cartTotalPrice).isLoyaltyCart) {
        yield put(globalActions.goto(payload.successRedirectUrl, true));
    } else {
        yield put(globalActions.redirect(payload.successRedirectUrl));
    }
}

function* watchPutPaymentMode() {
    yield takeEvery(types.PUT_PAYMENT_MODE, validationWrapper(putPaymentMode));
}

export function* paymentAvailability() {
    const termsAccepted = yield select(selectors.termsAccepted);
    yield put(actions.setPaymentAvailability(termsAccepted));
    const isOrderApiError = yield call(storage.get, storage.key.orderApiError);
    if (isOrderApiError) {
        yield put(actions.setOrderApiError(true));
    }
    yield call(storage.remove, storage.key.orderApiError);
}

function* watchLoaded() {
    yield takeEvery(cartTypes.SET_CART_DATA, paymentAvailability);
}

export function* termsToggleChange({ payload }) {
    if (payload.name === 'termsAccepted') {
        yield put(actions.setPaymentAvailability(payload.value));
        yield call(analytics.handleTermsChange, payload.value);
    }
}

function* watchTermsToggle() {
    yield takeEvery(types.TOGGLE_FIELD_VALUE, termsToggleChange);
}

export function* payWithPoints({ payload: { usePoints, points } }) {
    const userId = yield call(getUserId);
    const cartId = yield select(userId ? cartSelectors.getCode : cartSelectors.getGuid);
    const enableMinimumOrderThresholdForPWP = yield select(
        configSelectors.getMinimumOrderThresholdForPWP,
    );
    points = Math.abs(points / 100) * 100;
    try {
        yield call(api.payWithPoints, userId, cartId, usePoints, points);
        yield put(cartActions.loadCart());
        yield take(cartTypes.SET_CART_DATA);
        if (enableMinimumOrderThresholdForPWP) {
            const deliveryCost = yield select(cartSelectors.getDeliveryCost);
            const isCheckoutBlocked = yield select(cartSelectors.isCheckoutBlocked);

            if (deliveryCost.value == 0 && isCheckoutBlocked) {
                yield put(uiActions.scrollToTopOfPage());
                return yield put(actions.setError(paymentErrors.minimumOrderThresholdForPWP));
            }
            return yield put(actions.setError(paymentErrors.minimumOrderThresholdForPWP, false));
        }
    } catch (e) {
        yield call(logger.error, e);
    }
}

function* watchPayWithPoints() {
    yield takeEvery(types.PAY_WITH_LOYALTY_POINTS, payWithPoints);
}

export function* onSetMethod({ payload = {} }) {
    if (!payload) return;
    yield put(globalActions.setOriginAction(types.SET_PAYMENT_METHOD));
    yield call(analytics.handleSelectPaymentMethod, payload.name);
    const requestOnPaymentSelection = yield select(configSelectors.requestOnPaymentSelection);
    if (!requestOnPaymentSelection) return;

    const userId = yield call(getUserId);
    const isEgiftCardCartActive = !!(yield call(getEGCcartId)) || payload.useCartGuid;

    const cartId = yield call(getCartId, userId, payload.useCartGuid || isEgiftCardCartActive);

    yield put(actions.clearPaymentSession());

    try {
        yield call(api.setPaymentMethod, userId, cartId, payload.code, isEgiftCardCartActive);
        yield put(cartActions.loadCart());
        yield put(actions.paymentMethodSet());
    } catch (e) {
        yield call(logger.error, e);
        yield call(deletePaymentMethod);
    }

    const selectedPaymentMethod = yield select(selectors.getSelectedMethod);
    if (
        selectedPaymentMethod.isKlarnaAuth &&
        selectedPaymentMethod.code &&
        selectedPaymentMethod.integrationType === paymentSlots.REDIRECT &&
        selectedPaymentMethod.code.includes(paymentSlots.KLARNA)
    ) {
        yield put(actions.updatePaymentSession(selectedPaymentMethod));
    }
}

export function* updateSavedCard() {
    yield put(uiActions.openDialog({ name: 'edit-credit-card-popup' }));
}

function* watchSetMethod() {
    yield takeEvery(types.SET_PAYMENT_METHOD, onSetMethod);
}

function* watchSavedCardMethod() {
    yield takeEvery(types.EDIT_SAVED_CARD, updateSavedCard);
}

export function* updatePaymentSession({ payload = {} }) {
    const userId = yield call(getUserId);
    const isEgiftCardCartActive = !!(yield call(getEGCcartId));
    const cartId = yield call(getCartId, userId, isEgiftCardCartActive);

    yield put(actions.payNowActive(true));

    try {
        const sessionData = yield call(
            api.getPaymentSession,
            userId,
            cartId,
            payload.code,
            isEgiftCardCartActive,
        );
        if (!sessionData.success) {
            yield call(deletePaymentMethod);
            yield put(uiActions.scrollToTopOfPage());
            yield put(actions.setError(paymentErrors.getSessionError));
        }
        yield put(actions.setPaymentSession(sessionData));
        if (payload.code.includes(paymentSlots.KLARNA)) {
            if (
                !(
                    Array.isArray(sessionData.paymentCategories) &&
                    sessionData.paymentCategories.length
                )
            ) {
                yield call(deletePaymentMethod);
                yield put(uiActions.scrollToTopOfPage());
                yield put(actions.setError(paymentErrors.getSessionError));
            } else {
                const isLoaded = yield call(klarnaInitLoadWidget, sessionData);
                if (!isLoaded) {
                    yield put(actions.payNowActive(false));
                }
            }
        }
    } catch (e) {
        yield call(logger.error, e);
        yield call(deletePaymentMethod);
        yield put(uiActions.scrollToTopOfPage());
        yield put(actions.setError(paymentErrors.getSessionError));
    } finally {
        yield put(actions.payNowActive(false));
    }
}

function* deletePaymentMethod() {
    const userId = yield call(getUserId);
    const isEgiftCardCartActive = !!(yield call(getEGCcartId));
    const cartId = yield call(getCartId, userId, isEgiftCardCartActive);

    yield call(api.deletePaymentMethod, userId, cartId, isEgiftCardCartActive);
    yield put(cartActions.loadCart());
    yield put(actions.resetPaymentMethod());
}

function* watchUpdatePaymentSession() {
    yield takeEvery(types.UPDATE_PAYMENT_SESSION, updatePaymentSession);
}

export function* onPlaceOrder({ payload = {} }) {
    yield put(actions.resetErrors());
    yield put(actions.setOrderApiError(false));
    const isAnonymous = yield select(userSelectors.isAnonymous);
    const newOrderAPI = yield select(configSelectors.newOrderAPI);
    const isNewOrderApiEnabled = yield select(selectors.getIsNewOrderApiEnabled);
    const selectedPaymentMethod = yield select(selectors.getSelectedMethod);
    const paymentMethodSet = yield select(selectors.getPaymentMethodSet);
    const isPaymentAvailable = yield select(cartSelectors.isPaymentAvailable);
    const paymentAddress = yield select(cartSelectors.getPaymentAddress);
    const isEgiftCardCartActive = !!(yield call(getEGCcartId));
    const enableDistanceSellingContract = yield select(configSelectors.distanceSellingContract);
    const enablePreliminaryInformationForm = yield select(
        configSelectors.preliminaryInformationForm,
    );
    const distanceSellingContract = yield select(selectors.getdistanceSellingContract);
    const preliminaryInformationForm = yield select(selectors.getPreliminaryInformationForm);
    const cartNotFound = yield select(selectors.getCartNotFound);

    if (cartNotFound) {
        yield put(uiActions.scrollToTopOfPage());
        yield put(actions.setError(paymentErrors.cartNotFound));
        yield put(actions.setCartNotFound(false));
        return;
    }

    if (isEgiftCardCartActive && isEmpty(paymentAddress)) {
        yield put(uiActions.scrollToTopOfPage());
        return yield put(actions.setError(paymentErrors.billingAddressNotSelected));
    }

    if (!(selectedPaymentMethod.code && paymentMethodSet) && isPaymentAvailable) {
        yield put(uiActions.scrollToTopOfPage());
        return yield put(actions.setError(paymentErrors.paymentNotSelected));
    }

    const PrivacySelection = yield select(configSelectors.getPrivacySelection);

    if (isAnonymous || PrivacySelection.isPrivacyDefault) {
        yield put(actions.setTerms(true));
    }

    if (
        (enableDistanceSellingContract && !distanceSellingContract) ||
        (enablePreliminaryInformationForm && !preliminaryInformationForm)
    ) {
        yield put(uiActions.scrollToTopOfPage());
        return yield put(actions.setError(paymentErrors.agreementsNotAccepted));
    }

    const termsCheck = yield select(selectors.termsAccepted);

    if (!termsCheck) {
        yield put(uiActions.scrollToTopOfPage());
        return yield put(actions.setError(paymentErrors.termsNotAccepted));
    }

    const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);
    const cartType = yield select(cartSelectors.getCartType);
    const cartTotalPrice = yield select(cartSelectors.getTotalPrice);
    if (!evalCartType(cartType, cartTotalPrice).isDigitalCart && isEmpty(deliveryAddress)) {
        yield put(uiActions.scrollToTopOfPage());
        yield put(actions.setError(paymentErrors.deliveryStoreNotSelected));
        yield put(pickUpInStoreActions.setProvince(''));
        return;
    }

    const userId = yield call(getUserId);
    const cartId = yield call(getCartId, userId, isEgiftCardCartActive);

    yield put(analyticsActions.checkoutPaymentEvents());
    yield put(analyticsActions.setPaymentOptionCompleted());

    if (newOrderAPI || isNewOrderApiEnabled) {
        yield call(placeNewOrder, userId, cartId, payload.successRedirectUrl);
    } else {
        yield call(placeOrder, userId, cartId);
        yield put(globalActions.redirect(payload.successRedirectUrl));
    }
    yield call(checkPaymentError);
}

function* checkPaymentError() {
    const paymentErrors = yield select(paymentSelectors.getPaymentErrors);
    if (!paymentErrors.hasOwnProperty('klarnaAuthorizationError')) {
        yield put(actions.resetPaymentMethod());
    }
}

function* watchPlaceOrder() {
    yield takeEvery(types.PLACE_ORDER, validationWrapper(onPlaceOrder, true));
}

function* getSavedCards() {
    const userId = yield call(getUserId);
    const isEgiftCardCartActive = !!(yield call(getEGCcartId));
    const cartId = yield call(getCartId, userId, isEgiftCardCartActive);
    try {
        const { payments = [], savedSubscriptionsAvailable } = yield call(
            api.getSavedTokenCards,
            userId,
            cartId,
            isEgiftCardCartActive,
        );
        yield put(actions.setSavedCards(payments));
        yield put(actions.setSavedCardsAvailable(!!savedSubscriptionsAvailable));
    } catch (e) {
        yield call(logger.error, e);
    }
}

function* getMonetaWebIFrameUrl() {
    const userId = yield call(getUserId);
    try {
        yield put(actions.setLoading(true));
        const responseObj = yield call(api.fetchMonetaWebIFrameUrl, userId);
        yield put(globalActions.redirect(responseObj.redirectURL));
    } catch (e) {
        yield call(logger.error, e);
        yield put(actions.setError(paymentErrors.iFrameError));
    } finally {
        yield put(actions.setLoading(false));
    }
}

function* watchGetMonetaWebIFrameUrl() {
    yield takeEvery(types.FETCH_MONETAWEB_IFRAME_URL, getMonetaWebIFrameUrl);
}

function* watchGetSavedCards() {
    yield takeEvery(types.GET_SAVED_CARDS, getSavedCards);
}

function* fetchIFrameUrl({ payload }) {
    const userId = yield call(getUserId);
    const { id } = payload;
    const isEgiftCardCartActive = !!(yield call(getEGCcartId));
    const cartId = yield call(getCartId, userId, isEgiftCardCartActive);
    const body = {};
    if (id) {
        body.paymentInfoId = id;
    }
    try {
        yield put(actions.setLoading(true));
        const { iframeLocation, token } = yield call(api.fetchIFrameUrl, body, userId, cartId);
        yield put(actions.setIframeUrl({ url: iframeLocation, token }));
    } catch (e) {
        yield call(logger.error, e);
        const isUsedCard = yield select(selectors.getSaveCardUsed);
        if (isUsedCard) {
            yield put(actions.setError(paymentErrors.iFrameSaveCardError));
        } else {
            yield put(actions.setError(paymentErrors.iFrameError));
        }
    } finally {
        yield put(actions.setLoading(false));
    }
}

function* watchFetchIFrameCards() {
    yield takeEvery(types.FETCH_IFRAME_URL, fetchIFrameUrl);
}

function* removeSelectedCard() {
    const userId = yield call(getUserId);
    const cardId = yield select(selectors.getRemoveCardId);
    const response = yield call(api.removeCard, userId, cardId);
    if (!response.ok) {
        yield put(actions.setError(paymentErrors.removeCardFailed));
    } else {
        yield put(actions.setError(paymentErrors.removeCardSuccess));
    }
    yield call(getSavedCards);
    yield put(uiActions.closeDialog());
}

function* watchRemoveSelectedCard() {
    yield takeEvery(types.REMOVE_SELECTED_CARD, removeSelectedCard);
}

function* modifyDeliveryInformation() {
    const eGiftCardPDPLink = yield call(storage.get, storage.key.eGiftCardPDPLink);

    const baseSiteId = yield select(globalSelectors.getSite);
    const cookieName = `${baseSiteId}${cookieNames.eGiftCardCartEnding}`;
    const eGiftCartCookie = yield call(getCookie, cookieName);
    yield call(setCookie, `${cookieName}-temp`, eGiftCartCookie, 365);

    yield put(uiActions.closeDialogByName('confirm-modify-delivery-dialog'));
    yield put(cartActions.restoreRegularCart());
    yield put(cartActions.loadCart({ skipCreation: true }));
    yield put(globalActions.goto(eGiftCardPDPLink, true));
}

function* watchModifyDeliveryInformation() {
    yield takeEvery(types.MODIFY_DELIVERY_INFORMATION, modifyDeliveryInformation);
}

function* getGiftCardMethods() {
    const userId = yield call(getUserId);
    const cartId = yield select(userId ? cartSelectors.getCode : cartSelectors.getGuid);
    try {
        const giftCardMethods = yield call(api.getGiftCardMethods, userId, cartId);
        yield put(actions.setGiftCardMethods(giftCardMethods));
    } catch (e) {
        yield call(logger.error, e);
    }
}

function* watchGetGiftCardMethods() {
    yield takeEvery(types.GET_GIFT_CARD_METHODS, getGiftCardMethods);
}

function* getCartId(userId, isEgiftCardCartActive) {
    let cartId = yield select(userId ? cartSelectors.getCode : cartSelectors.getGuid);

    if (isEgiftCardCartActive) {
        cartId = yield select(cartSelectors.getGuid);
    }

    return cartId;
}

function* placeSwipeToBuyOrder() {
    const selectedCard = yield select(myAccountSelectors.getSelectedCard);

    const distanceSellingContract = yield select(selectors.getdistanceSellingContract);
    const preliminaryInformationForm = yield select(selectors.getPreliminaryInformationForm);
    const termsCheck = distanceSellingContract && preliminaryInformationForm;

    const errorType = yield call(
        validateSwipeToBuyData,
        selectedCard,
        termsCheck,
        distanceSellingContract,
        preliminaryInformationForm,
    );

    if (errorType) {
        return yield put(myAccountActions.setValidationError(errorType));
    }

    // Proceed with placing order
    yield put(myAccountActions.setValidationError(''));
    yield put(cartActions.closeSwipeToBuyDialog());

    const userId = yield call(getUserId);
    const cartId = yield call(getCartId, userId, false);
    const detectedDevice = yield call(getMobilePlatformType);

    yield put(analyticsActions.checkoutPaymentEvents());
    yield put(analyticsActions.setPaymentOptionCompleted());

    const body = yield call(
        buildSwipeToBuyRequestBody,
        distanceSellingContract,
        preliminaryInformationForm,
        selectedCard,
        termsCheck,
        detectedDevice,
    );

    try {
        yield put(actions.setLoading(true));
        const orderResponse = yield call(
            api.placeOrder,
            userId,
            cartId,
            false,
            body,
            true,
            !detectedDevice,
        );
        if (orderResponse.paymentUrl) {
            yield put(globalActions.goto('/checkout/multi/payment-method/next'));
        } else {
            yield call(analytics.handleTransaction, orderResponse);
            yield put(myAccountActions.setSwipeOrderConfirmationCode(orderResponse.code));
            yield put(orderActions.setOrder(orderResponse));
            yield put(uiActions.openDialog({ name: 'swipe-to-buy-order-confirmation' }));
            yield put(cartActions.managePostProcessForSwipeToBuy());
        }
    } catch (error) {
        yield call(
            handlePlaceOrderFailureScenario,
            error,
            '/checkout/multi/payment-method/choose',
            false,
        );
        yield call(deletePaymentMethod);
        yield call(storage.remove, 'swipe-to-buy-cart-total');
        yield call(storage.remove, 'cart-id-swipe-to-buy');
        const isPaymentSpecificError = yield select(selectors.getPaymentSpecificError);
        if (!isPaymentSpecificError) {
            yield put(actions.setError(paymentErrors.orderFailedText, error));
        }
        yield put(globalActions.goto('/checkout/multi/payment-method/choose'));
    } finally {
        yield put(actions.setLoading(false));
        yield put(actions.resetRequiredCheck());
        yield put(cartActions.loadCart());
        yield put(myAccountActions.setValidationError(''));
        yield put(myAccountActions.setSelectedCard({ cardId: '' }));
    }
}

function* buildSwipeToBuyRequestBody(
    distanceSellingContract,
    preliminaryInformationForm,
    selectedCard,
    termsCheck,
    detectedDevice,
) {
    const PrivacySelection = yield select(configSelectors.getPrivacySelection);
    const isTermsCheck = termsCheck || PrivacySelection.isPrivacyDefault;
    const deviceTypeFlag = yield select(configSelectors.deviceTypeFlag);
    const gdprServiceInformation = { termsCheck: isTermsCheck };
    gdprServiceInformation.privacyPolicyAccepted = PrivacySelection.isPrivacyDefault;
    return {
        gdprServiceInformation,
        legalInformation: [
            { name: 'distanceSellingContract', accepted: distanceSellingContract },
            { name: 'preliminaryInformationForm', accepted: preliminaryInformationForm },
        ],
        ...(deviceTypeFlag && { detectedDevice: detectedDevice !== '' ? 'MOBILE' : '' }),
        expressCheckout: true,
        additionalPaymentParameters: {
            entry: [
                {
                    key: 'paymentInfoId',
                    value: selectedCard,
                },
                {
                    key: 'userAgent',
                    value: navigator.userAgent,
                },
            ],
        },
    };
}

function* validateSwipeToBuyData(
    selectedCard,
    termsCheck,
    distanceSellingContract,
    preliminaryInformationForm,
) {
    const paymentAddress = yield select(cartSelectors.getPaymentAddress);
    const deliveryAddress = yield select(cartSelectors.getDeliveryAddress);
    const enableDistanceSellingContract = yield select(configSelectors.distanceSellingContract);
    const enablePreliminaryInformationForm = yield select(
        configSelectors.preliminaryInformationForm,
    );

    if (isEmpty(paymentAddress)) {
        return paymentErrors.billingAddressNotSelected;
    }
    if (isEmpty(deliveryAddress)) {
        return paymentErrors.shippingAddressNotSelected;
    }
    if (!selectedCard) {
        return paymentErrors.cardNotSelected;
    }
    if (
        (enableDistanceSellingContract && !distanceSellingContract) ||
        (enablePreliminaryInformationForm && !preliminaryInformationForm)
    ) {
        return paymentErrors.agreementsNotAccepted;
    }
    if (!termsCheck) {
        return paymentErrors.termsNotAccepted;
    }
    return false;
}

function* loadKlarnaApiSaga() {
    try {
        yield call(klarnaApiLoader.load);
    } catch (e) {
        yield call(logger.error, e);
    }
}

function* watchKlarnaApiLoader() {
    yield takeEvery(types.LOAD_KLARNA_API, loadKlarnaApiSaga);
}

function* watchSwipeToBuyPlaceOrder() {
    yield takeLatest(types.PLACE_ORDER_SWIPE_TO_BUY, placeSwipeToBuyOrder);
}

function* watchLoyaltyPointsPayMethod() {
    yield takeLatest(types.GET_PAY_WITH_LOYALTY_POINTS_METHOD, setLoyaltyPointsPayMethod);
}

export default [
    watchLoaded(),
    watchGetPaymentMethods(),
    watchGetMonetaWebIFrameUrl(),
    watchGetSavedCards(),
    watchFetchIFrameCards(),
    watchPutPaymentMode(),
    watchTermsToggle(),
    watchLoyaltyPointsPayMethod(),
    watchPayWithPoints(),
    watchSetMethod(),
    watchSavedCardMethod(),
    watchPlaceOrder(),
    watchRemoveSelectedCard(),
    watchModifyDeliveryInformation(),
    watchGetGiftCardMethods(),
    watchUpdatePaymentSession(),
    watchKlarnaApiLoader(),
    watchSwipeToBuyPlaceOrder(),
];
